import React from 'react';
import { Briefcase, MapPin, DollarSign, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';

const ClientCard = ({ 
  client, 
  expandedClient, 
  toggleClientExpand, 
  handleOpenInClientInsights,
  processInterests 
}) => {
  const isExpanded = expandedClient === client.id;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden hover:border-blue-300 transition-colors">
      <div
        className="p-4 cursor-pointer"
        onClick={() => toggleClientExpand(client.id)}
      >
        <div className="flex items-center">
          <div className="h-12 w-12 rounded-full overflow-hidden mr-4 flex-shrink-0">
            <img
              src={client.image}
              alt={client.name}
              className="h-full w-full object-cover"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://via.placeholder.com/150?text=No+Image";
              }}
            />
          </div>

          <div className="flex-grow">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-medium text-gray-900">{client.name}</h3>
                <p className="text-sm text-gray-500 flex items-center">
                  <Briefcase size={14} className="mr-1" />
                  {client.company} • {client.role}
                </p>
              </div>

              <div className="flex items-center">
                {isExpanded ? (
                  <ChevronUp size={18} className="text-gray-500" />
                ) : (
                  <ChevronDown size={18} className="text-gray-500" />
                )}
              </div>
            </div>

            <div className="mt-2 flex flex-wrap gap-2">
              {client.personas && client.personas.map((persona, idx) => (
                <span key={idx} className="bg-purple-50 text-purple-700 text-xs px-2 py-0.5 rounded">
                  {persona}
                </span>
              ))}
              <span className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded flex items-center">
                <MapPin size={12} className="mr-1" />
                {client.location}
              </span>
              {client.assetTier && (
                <span className="bg-green-50 text-green-700 text-xs px-2 py-0.5 rounded flex items-center">
                  <DollarSign size={12} className="mr-1" />
                  {client.assetTier}
                </span>
              )}
              {client.industry && (
                <span className="bg-yellow-50 text-yellow-700 text-xs px-2 py-0.5 rounded">
                  {client.industry}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {isExpanded && (
        <div className="border-t border-gray-200 bg-gray-50 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-white p-3 rounded border border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Background</h4>
              <p className="text-sm text-gray-600">{client.details?.background || "No background information available."}</p>
            </div>
            <div className="bg-white p-3 rounded border border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Financial Profile</h4>
              <p className="text-sm text-gray-600">{client.details?.financialProfile || "No financial profile available."}</p>
            </div>
          </div>

          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Interests</h4>
            <div className="flex flex-wrap gap-2">
              {client.interests && processInterests(client.interests).length > 0 ? (
                processInterests(client.interests).map((interest, idx) => (
                  <span key={idx} className="bg-blue-50 text-primaryDark text-xs px-2 py-0.5 rounded">
                    {interest}
                  </span>
                ))
              ) : (
                <span className="text-sm text-gray-500 italic">
                  {Array.isArray(client.interests) && client.interests.some(i => i === true)
                    ? "Client has interests, but details are not available"
                    : "No specific interests recorded"}
                </span>
              )}
            </div>
          </div>

          <div className="flex justify-between items-center">
            <div></div>
            <div className="flex gap-2">
              <button
                className="bg-blue-600 rounded-md px-3 py-1.5 text-sm text-white hover:bg-blue-700 transition-colors flex items-center"
                onClick={() => handleOpenInClientInsights(client)}
              >
                Open In Client Insights <ArrowRight size={14} className="ml-1.5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientCard;